// src/api/provider-throttle.ts
//
// Admin-only provider call limits for measurement batteries. Stored on the
// backend in app_config, so a change takes effect without a deploy — the
// measurement workers re-read within ~30s, a run already in flight included.
//
// The bounds and the Perplexity tier presets come DOWN with the GET rather
// than being duplicated here: Perplexity's published per-tier allowances are
// the backend's business, and a copy in the UI is a copy that goes stale.
import { apiFetch } from "./api-fetch";

export interface ProviderThrottleLimits {
  min_qps: number;
  max_qps: number;
  min_concurrent_calls: number;
  max_concurrent_calls: number;
  min_retries: number;
  max_retries: number;
}

export interface ProviderThrottleSettings {
  /** Perplexity Agent API start rate, queries per second. 0 = no pacing. */
  perplexity_agent_qps: number;
  /** Calls in flight per provider. 0 = uncapped. */
  max_concurrent_calls: number;
  /** Extra attempts a 429'd call gets before it counts as a failure. */
  rate_limit_max_retries: number;
  limits: ProviderThrottleLimits;
  /** Perplexity tier -> its published QPS allowance, keyed by tier number. */
  perplexity_tier_qps: Record<string, number>;
}

/** Any field omitted is left as it is — changing the start rate must not
 *  reset the concurrency cap. */
export interface ProviderThrottleUpdate {
  perplexity_agent_qps?: number;
  max_concurrent_calls?: number;
  rate_limit_max_retries?: number;
}

export const providerThrottleApi = {
  get: () => apiFetch<ProviderThrottleSettings>("/admin/provider-throttle"),

  update: (changes: ProviderThrottleUpdate) =>
    apiFetch<ProviderThrottleSettings>("/admin/provider-throttle", {
      method: "PUT",
      body: JSON.stringify(changes),
    }),
};
